import type {
  ConsultantClient,
  ConsultantClientFollower,
  ConsultantClientManager,
} from '../../../services/consultant/consultant.service.interface';
import { getLocale } from '../../../utils/get-locale';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** "Gestita da te" for the reader's own companies, otherwise the colleague who added it. */
export function resolveManagedChipLabel(
  managed: {
    isManagedByMe: boolean;
    managedBy: Pick<ConsultantClientManager, 'displayName'> | null;
  },
  t: Translate,
): string {
  if (managed.isManagedByMe) {
    return t('consultant.clients.managed.chip');
  }
  if (managed.managedBy) {
    return t('consultant.clients.managedBy', { name: managed.managedBy.displayName });
  }
  return t('consultant.clients.managedByAnother');
}

/**
 * Who follows the company, in plain words: "La segui tu", "La segue Mario",
 * "La seguono Mario e Lucia". Null when nobody follows it (e.g. a managed company).
 */
export function resolveFollowersLabel(
  followers: ConsultantClientFollower[],
  t: Translate,
): string | null {
  const readerFollows = followers.some((follower) => follower.isMe);
  const colleagueNames = followers
    .filter((follower) => !follower.isMe)
    .map((follower) => follower.displayName);
  const names = new Intl.ListFormat(getLocale(), { style: 'long', type: 'conjunction' }).format(
    colleagueNames,
  );
  if (readerFollows) {
    return colleagueNames.length === 0
      ? t('consultant.clients.followedByYou')
      : t('consultant.clients.followedByYouAnd', { names });
  }
  if (colleagueNames.length === 0) {
    return null;
  }
  return colleagueNames.length === 1
    ? t('consultant.clients.followedBy', { name: names })
    : t('consultant.clients.followedByMany', { names });
}

/**
 * A credited period reads TRIALING on the server while the client is fully
 * paid: showing "prova" would misinform the consultant about a paying client.
 */
export function resolveDisplayStatus(
  client: Pick<ConsultantClient, 'creditedUntil' | 'subscriptionStatus'>,
): string {
  return client.creditedUntil ? 'ACTIVE' : client.subscriptionStatus;
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(getLocale(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * A credited period wins over the trial date: the client already paid, and its
 * `trialEndsAt` is the stale date of the original trial. `trialEndsAt` is read
 * only while the server status is still TRIALING: once a tenant converts, the
 * server may still carry a stale (already-past) trial date, and a paying
 * client must never read "Prova fino al <past date>".
 */
export function resolveExpiryLabel(
  client: ConsultantClient,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  if (client.creditedUntil) {
    return t('consultant.clients.paidUntil', { date: formatDate(client.creditedUntil) });
  }
  if (client.subscriptionStatus === 'TRIALING' && client.trialEndsAt) {
    return t('consultant.clients.trialEndsAt', { date: formatDate(client.trialEndsAt) });
  }
  if (client.currentPeriodEnd) {
    // A period-end date on a tenant that scheduled a cancellation is not a
    // renewal: saying "Rinnovo il X" on a client who is leaving misinforms
    // the consultant about their own portfolio.
    return client.cancelAtPeriodEnd
      ? t('consultant.clients.periodEndsCanceled', { date: formatDate(client.currentPeriodEnd) })
      : t('consultant.clients.periodEnd', { date: formatDate(client.currentPeriodEnd) });
  }
  return null;
}
